import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import DashboardView from 'screens/User/Dashboard/components/View';
import MapModal from 'components/Modal/MapModal';
import handlePromise from 'utils/Promise';
import ProjectService from 'services/api/Projects';
import { useSelector } from 'react-redux';
import { selectUser, selectUserAccount } from 'store/User';
import { KpiAccount } from 'types/Account';
import { LoadingContext } from 'context/Loading';
import useFetchTableData from 'hooks/useFetchTableData';
import { getFile } from 'utils/downloadBase64';
import { FutureInvestment } from 'types/FutureInvestment';
import { ModalErrorContext } from 'context/ModalError';
import EventService from 'services/api/Event';
import InvitationsServices from 'services/api/Invitations';
import Session from 'services/session/Session';
import { useNavigate } from 'react-router';

function Dashboard() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  const { toggleLoading } = useContext( LoadingContext );
  const { openErrorModal } = useContext( ModalErrorContext );
  const currentUser = useSelector( selectUser );
  const userAccountId = useSelector( selectUserAccount );
  const [showMapModal, setMapModal] = useState( false );
  const [kpiAccount, setKpiAccount] = useState<KpiAccount | null>( null );
  const [projects, setProjects] = useState<[] | undefined>( );
  const [nextDeliveries, setNextDeliveries] = useState<[] | undefined>( );
  const [mapInfo, setMapInfo] = useState<[] | undefined>( );
  const [myCashFlow, setMyCashFlow] = useState<[] | undefined>( );
  const [latestReports, setLatestReports] = useState<[] | undefined>( );
  const [dataLoaded, setDataLoaded] = useState( false );
  const [futureInvestment, setFutureInvestment] = useState<FutureInvestment | null>( null );
  const [calendarEvents, setCalendarEvents] = useState<any[] | undefined>( [] );
  const [mapDetail, setMapDetail] = useState<{
    province: string,
    list: [],
    defaultCoors: {
      lat: number,
      lng: number,
    },
  }>( {
    province: '',
    list: [],
    defaultCoors: {
      lat: 0.0,
      lng: 0.0,
    },
  } );

  const openMapModal = useCallback( async ( { params }:{ params:any } ) => {
    toggleLoading( true );
    const [, mapDetailResponse, mapDetailData] = await handlePromise(
      ProjectService.getDetalleMapaByProvincia( userAccountId, params.province ),
    );
    if ( mapDetailResponse.ok && mapDetailData.length ) {
      const defaultCors = mapDetailData[0];
      setMapDetail( {
        province: params.province,
        defaultCoors: {
          lat: defaultCors.latitud,
          lng: defaultCors.longitud,
        },
        list: mapDetailData,
      } );
      setMapModal( true );
      setTimeout( () => {
        toggleLoading( false );
      }, 800 );
    }
  }, [userAccountId] );

  const closeMapModal = useCallback( ( ) => {
    setMapModal( false );
  }, [] );

  const downloadFile = useCallback( async ( type:'projects' | 'flow' ) => {
    toggleLoading( true );
    let downloadDocumentData;
    if ( type === 'projects' ) {
      [, , downloadDocumentData] = await handlePromise(
        ProjectService.getPdfResumenProyectos( userAccountId, { responseType: 'blob' } ),
      );
    } else {
      [, , downloadDocumentData] = await handlePromise(
        ProjectService.getInformePdfByCuentaInversion( userAccountId, { responseType: 'blob' } ),
      );
    }

    setTimeout( () => {
      toggleLoading( false );
    }, 800 );

    if ( downloadDocumentData ) {
      getFile( downloadDocumentData, type === 'projects' ? 'Resumen-proyectos' : 'Informe-por-cuenta-inversión', 'application/pdf', 'pdf' );
    }
  }, [userAccountId] );

  const opportunityInterestsme = useCallback( async ( ) => {
    toggleLoading( true );
    const [error, response, invitationNdaData] = await handlePromise(
      InvitationsServices.postCompartirDocumentosOportunidad(
        {
          email: currentUser.email as string,
          oportunidadId: futureInvestment?.futuraInversionId,
        },
        currentUser.accessToken as string,
      ),
    );

    if ( response.ok ) {
      if ( invitationNdaData?.token && invitationNdaData?.invitacionUid ) {
        Session.setToken( invitationNdaData.token );
        const url = `${window.location.origin}/link/informacion-confidencial/${invitationNdaData?.invitacionUid}`;
        window.open( url, '_blank', 'noopener' );
      }
    } else {
      openErrorModal( error.toString() );
    }

    setTimeout( () => {
      toggleLoading( false );
    }, 800 );
  }, [currentUser, futureInvestment] );

  const { fetchData, data, dataLoading } = useFetchTableData( {
    promise: ( params ) => ProjectService.getPromocionesActivos( userAccountId, params ),
    deps: [userAccountId],
  } );

  const getData = useCallback( async () => {
    setProjects( undefined );
    setMapInfo( undefined );
    setMyCashFlow( undefined );
    setLatestReports( undefined );
    setNextDeliveries( undefined );
    setCalendarEvents( undefined );

    const [, futuraInversionResponse, futuraInversionData] = await handlePromise(
      ProjectService.getFuturainversion( ),
    );

    if ( futuraInversionResponse?.ok ) {
      const futuraInversionArray = ( futuraInversionData?.data ?? [] ) as FutureInvestment[];

      const latestFuturaInversion: FutureInvestment | null = futuraInversionArray.length > 0
        ? futuraInversionArray.sort( ( a, b ) => new Date( b.createdAt ).getTime()
      - new Date( a.createdAt ).getTime() )[0]
        : null;
      setFutureInvestment( latestFuturaInversion );
    }

    /* const date = new Date();
    const month = date.getMonth() + 1;
    const year = date.getFullYear(); */
    const [, calendarEventsResponse, calendarEventsData] = await handlePromise(
      EventService.getEventByAccount( userAccountId as string ),
    );
    if ( calendarEventsResponse.ok ) {
      setCalendarEvents( calendarEventsData.map( ( item: any ) => ( {
        id: item.eventoId,
        title: item.titulo,
        start: new Date( item.fechaInicio ).toISOString().split( 'T' )[0],
        backgroundColor: '#BFCAEE',
        borderColor: '#263B78',
        ...item,
      } ) ) );
    }

    const [, kpiAccountResponse, kpiAccountData] = await handlePromise(
      ProjectService.getKPIPosicionGlobalByCuentaInversion( userAccountId ),
    );
    if ( kpiAccountResponse.ok ) setKpiAccount( kpiAccountData );

    const [, projectsResponse, projectsData] = await handlePromise(
      ProjectService.getPromocionesInvertidas( userAccountId ),
    );
    if ( projectsResponse.ok ) setProjects( projectsData );

    const [, mapDataRespose, mapData] = await handlePromise(
      ProjectService.getProvinciasMapa( userAccountId ),
    );
    if ( mapDataRespose.ok ) setMapInfo( mapData );

    const [, nextResponse, nextData] = await handlePromise(
      ProjectService.getProximosRepartos( userAccountId ),
    );
    if ( nextResponse.ok ) setNextDeliveries( nextData );

    const [, cashFlowResponse, cashFlow] = await handlePromise(
      ProjectService.getMiFlujoCajaEstimado( userAccountId ),
    );
    if ( cashFlowResponse.ok ) setMyCashFlow( cashFlow );

    const [, latestReportsResponse, latestReportsData] = await handlePromise(
      ProjectService.getUltimosInformesFinancieros( userAccountId ),
    );
    if ( latestReportsResponse.ok ) setLatestReports( latestReportsData );

    setTimeout( () => {
      setDataLoaded( true );
    }, 800 );
  }, [userAccountId] );

  useEffect( () => {
    getData();
  }, [userAccountId] );

  return (
    <>
      <DashboardView
        onOpenMapModal={openMapModal}
        kpiAccount={kpiAccount}
        projects={projects}
        mapInfo={mapInfo}
        futureInvestment={futureInvestment}
        myCashFlow={myCashFlow}
        latestReports={latestReports}
        nextDeliveries={nextDeliveries}
        calendarEvents={calendarEvents}
        dataLoaded={dataLoaded}
        activeProjects={data}
        fetchData={fetchData}
        isLoading={dataLoading}
        downloadFile={downloadFile}
        opportunityInterestsme={opportunityInterestsme}
      />
      <MapModal
        show={showMapModal}
        onClose={closeMapModal}
        mapDetail={mapDetail}
      />
    </>
  );
}

export default Dashboard;
